import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const links = [
  { label: "Modules", href: "#benefits" },
  { label: "Compare", href: "#compare" },
];

function Wordmark() {
  return (
    <a
      href="#top"
      className="font-display text-[22px] md:text-[24px] tracking-tight leading-none text-ink flex items-center gap-1.5"
      data-testid="navbar-wordmark"
    >
      <span className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center shrink-0">
        <img
          src="/probodh-icon-192.png"
          alt=""
          aria-hidden="true"
          className="w-full h-full object-contain"
        />
      </span>
      <span>ProBodh</span>
    </a>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-off/95 backdrop-blur-md border-b border-line-soft"
            : "bg-off/70 backdrop-blur-sm"
        }`}
        data-testid="site-navbar"
      >
        <div className="mx-auto max-w-[1280px] px-5 md:px-8 h-16 md:h-20 flex items-center justify-between gap-4">
          <Wordmark />

          <ul className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  className="text-[14px] font-medium text-ink-soft hover:text-ink transition-colors"
                  data-testid={`navbar-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          <div className="flex items-center gap-2">
            <a
              href="#contact"
              className="btn-pill btn-primary hidden sm:inline-flex"
              data-testid="navbar-cta"
            >
              Get in touch
            </a>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="lg:hidden w-10 h-10 flex items-center justify-center text-ink hover:bg-off rounded-full transition-colors"
              aria-label="Open menu"
              data-testid="navbar-mobile-open"
            >
              <Menu size={22} />
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[60] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            data-testid="mobile-drawer"
          >
            <div
              className="absolute inset-0 bg-ink/40"
              onClick={() => setOpen(false)}
            />
            <motion.aside
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="absolute top-0 right-0 h-full w-[86%] max-w-sm bg-off shadow-2xl flex flex-col"
            >
              <div className="h-16 px-5 flex items-center justify-between border-b border-line-soft">
                <Wordmark />
                <button
                  onClick={() => setOpen(false)}
                  className="w-10 h-10 flex items-center justify-center text-ink hover:bg-off rounded-full transition-colors"
                  aria-label="Close menu"
                  data-testid="navbar-mobile-close"
                >
                  <X size={20} />
                </button>
              </div>
              <ul className="flex-1 overflow-y-auto py-4">
                {links.map((l) => (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      onClick={() => setOpen(false)}
                      className="block px-5 py-4 font-display text-2xl text-ink hover:bg-off transition-colors"
                      data-testid={`navbar-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    >
                      {l.label}
                    </a>
                  </li>
                ))}
              </ul>
              <div className="p-5 border-t border-line-soft">
                <a
                  href="#contact"
                  onClick={() => setOpen(false)}
                  className="btn-pill btn-primary w-full"
                  data-testid="navbar-mobile-cta"
                >
                  Get in touch
                </a>
              </div>
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
